import { useMemo } from 'react';
import { UsePaginationLabelReturn } from './use-pagination-label';
import { getMiddlePageNumbers, getPageNumbers } from '../helpers';

export type UsePaginationReturn = {
    type: 'page' | 'ellipsis';
    pageNumber?: number;
};

export const usePagination = ({
    width,
    currentPage,
    totalPages,
}: {
    width: number;
} & Pick<UsePaginationLabelReturn, 'currentPage' | 'totalPages'>): UsePaginationReturn[] => {
    const initialPages = useMemo(() => {
        const pageNumbers = getPageNumbers(1, width);

        const pages = pageNumbers.map((n) => ({
            type: 'page',
            pageNumber: n,
        }));

        const ellipsis = {
            type: 'ellipsis',
        };

        const lastPage = {
            type: 'page',
            pageNumber: totalPages,
        };

        return [...pages, ellipsis, lastPage];
    }, [width, totalPages]);

    const middlePages = useMemo(() => {
        const firstPage = {
            type: 'page',
            pageNumber: 1,
        };

        const pageNumbers = getMiddlePageNumbers(currentPage, Math.ceil(width / 2) - 2);

        const pages = pageNumbers.map((n) => ({
            type: 'page',
            pageNumber: n,
        }));

        const ellipsis = {
            type: 'ellipsis',
        };

        const lastPage = {
            type: 'page',
            pageNumber: totalPages,
        };

        return [firstPage, ellipsis, ...pages, ellipsis, lastPage];
    }, [currentPage, width, totalPages]);

    const endPages = useMemo(() => {
        const firstPage = {
            type: 'page',
            pageNumber: 1,
        };

        const pageNumbers = getPageNumbers(totalPages - width + 1, totalPages);

        const pages = pageNumbers.map((n) => ({
            type: 'page',
            pageNumber: n,
        }));

        const ellipsis = {
            type: 'ellipsis',
        };

        return [firstPage, ellipsis, ...pages];
    }, [totalPages, width]);

    const allPages = useMemo(() => {
        const pageNumbers = getPageNumbers(1, totalPages);

        return pageNumbers.map((n) => ({
            type: 'page',
            pageNumber: n,
        }));
    }, [totalPages]);

    if (totalPages <= width + 1) {
        return allPages as UsePaginationReturn[];
    }

    if (currentPage < width) {
        return initialPages as UsePaginationReturn[];
    }

    if (currentPage >= width && currentPage <= totalPages - width + 1) {
        return middlePages as UsePaginationReturn[];
    }

    if (currentPage > totalPages - width) {
        return endPages as UsePaginationReturn[];
    }

    return allPages as UsePaginationReturn[];
};
